import React, {useMemo} from 'react';
import { X, Plus } from 'lucide-react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

const ResponsiveTabs = ({ 
  tabs = [], 
  activeTab = 0, 
  onTabChange = () => {}, 
  onTabClose = null,
  onAddTab = null,
  canAddMoreTabs = true,
  addTabLabel = 'Add another leg',
  maxTabs = 5,
  className = '',
  children 
}) => {

  const handleTabClick = (index) => {
    onTabChange(index);
  };


  const handleCloseTab = (index, e) => {
    e.stopPropagation();
    if (onTabClose) {
      onTabClose(index);
    }
  };

  const handleAddTab = () => {
    if (onAddTab) {
      onAddTab();
    }
  };

  return (
    <div className={`w-full bg-white ${className}`}>
      {/* Mobile View - Stacked */}
      <div className="md:hidden">
        {/* Current tab display */}
        <div className="flex flex-row">
          {tabs.map((tab, index) => (
            <div 
              key={tab.id || index} 
              className={`
                  relative flex items-center justify-between px-1 py-2 cursor-pointer
                  transition-all duration-200 min-w-0 flex-1
                  ${activeTab === index 
                    ? 'bg-white -mb-[2px] border-b-0 border-t border-l border-r border-gray-200 rounded-t-lg' 
                    : 'bg-white '
                  }
                  ${index === 0 ? 'rounded-tl-lg' : ''}
                  ${index === tabs.length - 1 ? 'rounded-tr-lg' : ''}
                `}
              onClick={() => handleTabClick(index)}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <div className="flex-1 min-w-0">
                    <div className={`flex flex-row justify-between items-center font-medium text-xs p-2 ${
                      activeTab === index ? 'text-gray-900' : 'text-gray-600'
                    }`}>
                      <h3 className="font-medium text-gray-900">
                        {tab?.title || `Tab ${activeTab + 1}`}
                      </h3>
                      {/* {tab?.closable && onTabClose && (
                        <button
                          onClick={(e) => handleCloseTab(activeTab, e)}
                          className="p-1 rounded-full hover:bg-gray-200 text-gray-500 hover:text-gray-700"
                        >
                          <X size={16} />
                        </button>
                      )} */}
                    </div>
                  </div>
                </div>
              </div>
              {activeTab === index && (
                <div className="absolute bottom-0 left-3 right-3 h-0.5 bg-green-500"></div>
              )}
            </div>
          ))}
          {/* Add tab button for mobile */}
          {onAddTab && tabs.length < maxTabs && (
            <button
              onClick={handleAddTab}
              disabled={!canAddMoreTabs}
              className={clsx(
                'flex items-center justify-center gap-2 p-3 border-2 ',
                'border-none border-gray-300 rounded-lg text-gray-600 ',
                'hover:text-gray-800 hover:border-gray-400 mb-4', { 'hover:text-gray-500 cursor-not-allowed': !canAddMoreTabs }
                )}
            >
              <Plus size={16} />
              <span className="text-sm font-medium">{addTabLabel}</span>
            </button>
          )}
        </div>
      </div>

      {/* Desktop View - Horizontal Tabs */}
      <div className="hidden md:block">
        {/* Tab Headers */}
        <div className=" flex border-b border-gray-200">
          {tabs.map((tab, index) => (
            <div
              key={tab.id || index}
              className={`
                relative flex items-center justify-between px-6 py-4 cursor-pointer
                transition-all duration-200 min-w-[40%]
                ${activeTab === index 
                  ? 'bg-white -mb-[2px] border-b-0 border-t border-l border-r border-gray-200 rounded-t-lg' 
                  : 'bg-white hover:bg-gray-100'
                }
                ${index === 0 ? 'rounded-tl-lg' : ''}
                ${index === tabs.length - 1 ? 'rounded-tr-lg' : ''}
              `}
              onClick={() => handleTabClick(index)}
            >
              <div className="flex-1 min-w-0">
                <div className="flex  flex-row justify-center items-center gap-3">
                  <div className="flex flex-row gap-3 justify-around min-w-0">
                    <div className={`font-medium text-lg ${
                      activeTab === index ? 'text-gray-900' : 'text-gray-600'
                    }`}>
                      {tab.title}
                    </div>
                    <div className="flex flex-col ml-2 min-w-0">
                      {tab.subtitle && (
                        <div className={`text-xs text-slate-600 hover:text-slate-700 cursor-pointer truncate ${
                          activeTab === index ? 'text-slate-600' : 'text-slate-500'
                        }`}>
                          {tab.subtitle}
                        </div>
                      )}
                      {tab.price && (
                        <div className={`text-xs font-medium ${
                          activeTab === index ? 'text-gray-700' : 'text-gray-500'
                        }`}>
                          Price: {tab.price}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {tab.closable && onTabClose && (
                    <button
                      onClick={(e) => handleCloseTab(index, e)}
                      className={`
                        p-1 rounded-full hover:bg-gray-200 transition-colors
                        ${activeTab === index ? 
                        'text-gray-500 hover:text-gray-700' : 'text-gray-400 hover:text-gray-600'}
                      `}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>
              
              {/* Active tab indicator */}
              {activeTab === index && (
                <div className="absolute bottom-0 left-5 right-5 h-0.5 bg-green-500"></div>
              )}
            </div>
          ))}
          
          {/* Add new tab button */}
          {onAddTab && tabs.length < maxTabs && (
            <div className="flex-shrink-0 flex items-center px-4">
              <button 
                onClick={handleAddTab}
                disabled={!canAddMoreTabs}
                className={clsx(
                  'flex items-center gap-1 text-gray-500 hover:text-gray-700 ',
                  'text-sm font-medium transition-colors', { 'hover:text-gray-500 cursor-not-allowed': !canAddMoreTabs }
                )}
              >
                <Plus size={14} />
                {addTabLabel}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Shared Tab Content - renders only once */}
      <div className={clsx(
        'bg-white md:border-2 md:border-t-0 md:border-gray-200 md:rounded-b-lg ',
        'md:p-4 border-t-2 border-gray-200'
        )}>
        {children}
      </div>
    </div>
  );
};

export default ResponsiveTabs;

ResponsiveTabs.propTypes = {
  /** Array of tab objects */
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string.isRequired,
      subtitle: PropTypes.string,
      price: PropTypes.string,
      closable: PropTypes.bool
    })
  ).isRequired,
  
  activeTab: PropTypes.number,
  onTabChange: PropTypes.func,
  onTabClose: PropTypes.func,
  onAddTab: PropTypes.func,
  addTabLabel: PropTypes.string,
  canAddMoreTabs: PropTypes.bool,
  maxTabs: PropTypes.number,
  className: PropTypes.string,
  children: PropTypes.node
};